import { z } from "zod";
import { fatorDeCorrecaoSchema, quantidadeSchema } from "./material";

/**
 * Ficha técnica — o que um serviço consome.
 *
 * O par serviço × material do cardápio é a mesma chave de `LabPriceListItem`, então receita e
 * custo compartilham a chave e a margem sai direto. Mas o par pode ter VÁRIAS fichas: "Coroa
 * impressa (BioCrown)" e "Coroa impressa (BioCrown Diamond)" são o mesmo par com receitas e custos
 * diferentes. É por isso que a ficha tem nome, e é por id que ela é endereçada.
 *
 * A chave é a OFERTA (`LabMaterialOption`), o que o dentista escolheu. Já cada LINHA em aberto
 * aponta pra química (`MaterialCategory`), que é de onde saem os candidatos na bancada — são
 * coisas diferentes de propósito.
 */

export const fichaTecnicaChaveSchema = z.object({
  serviceCategoryId: z.string().min(1),
  /** Null/ausente = fichas que valem pra qualquer material daquele serviço. */
  materialOptionId: z.string().min(1).nullish(),
});

export const fichaTecnicaIdSchema = z.object({ id: z.string().min(1) });

/**
 * Cada linha aponta pra UM dos dois — e é essa escolha que cria a regra, sem flag de configuração:
 * material exato (broca, embalagem) ou categoria (decisão resolvida na bancada).
 *
 * O banco tem a mesma restrição num CHECK; aqui ela existe pra a mensagem de erro ser legível.
 */
export const fichaTecnicaLinhaSchema = z
  .object({
    materialLineId: z.string().min(1).nullish(),
    materialCategoryId: z.string().min(1).nullish(),
    quantityNet: quantidadeSchema,
    correctionFactor: fatorDeCorrecaoSchema.default(1),
  })
  .refine((l) => !!l.materialLineId !== !!l.materialCategoryId, {
    message: "Cada linha aponta para um material específico OU para uma categoria, nunca os dois.",
  });

export const fichaTecnicaSalvarSchema = fichaTecnicaChaveSchema.extend({
  /** Ausente = ficha nova. Presente = atualiza aquela ficha. */
  id: z.string().min(1).optional(),
  name: z.string().trim().min(2, "Dê um nome à ficha").max(80),
  notes: z.string().trim().max(2000).nullish(),
  lines: z.array(fichaTecnicaLinhaSchema).max(100),
  /** Máquinas que a receita assume. O custo delas é o consumível, resolvido pelo material desta
   *  ficha — ver `custo-equipamento`. */
  equipmentIds: z.array(z.string().min(1)).max(20).default([]),
});
export type FichaTecnicaSalvarInput = z.infer<typeof fichaTecnicaSalvarSchema>;
